import React, { useState, useEffect, useMemo } from "react";
import { toast } from 'react-toastify';
import './Admin.css'; // Assuming Admin.css can be reused or will be extended
import { User } from "./types";
import EditUserEmailModal from './EditUserEmailModal'; // Import the modal component

const formatDate = (dateString?: string | Date): string => {
  if (!dateString) return 'N/A';
  try {
    // Ensure toLocaleString is called on a Date object
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return date.toLocaleString();
  } catch (e) {
    return 'Invalid Date';
  }
};

const UsersTab: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);
  const [isGeneratingLink, setIsGeneratingLink] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showUserList, setShowUserList] = useState<boolean>(false);

  // State for sorting and pagination
  const [sortField, setSortField] = useState<keyof User | string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(1);

  // State for EditUserModal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // TODO: Add state for EditUserModal visibility and user being edited
  // const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  // const [editingUser, setEditingUser] = useState<User | null>(null);

  useEffect(() => {
    setIsLoadingUsers(true);
    fetch(`${process.env.REACT_APP_ADMIN_API_URL}/api/get-users`, {
        headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` }
    })
    .then(response => {
        if (!response.ok) {
            throw new Error('Failed to fetch users');
        }
        return response.json();
    })
    .then(data => {
        setUsers(data.users || []);
        setIsLoadingUsers(false);
    })
    .catch(error => {
        console.error("Error fetching users:", error);
        toast.error("Failed to fetch users.");
        setIsLoadingUsers(false);
    });
  }, []);

  const handleGenerateLink = async () => {
    if (!selectedUserId) {
      toast.warn("Please select a user.");
      return;
    }
    setIsGeneratingLink(true);
    setGeneratedLink(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_ADMIN_API_URL}/api/generate-auth-link`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ userId: selectedUserId }),
      });
      if (response.ok) {
        const data = await response.json();
        setGeneratedLink(data.link);
        toast.success("Access link generated!");
      } else {
        toast.error("Failed to generate link.");
      }
    } catch (error) {
      toast.error("Error generating link: " + error);
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const handleSort = (field: keyof User | string) => {
    const newDirection = field === sortField && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
  };

  const sortedUsers = useMemo(() => {
    if (!users) return [];
    return [...users].sort((a, b) => {
      const valA = a[sortField as keyof User];
      const valB = b[sortField as keyof User];

      // Handle date sorting specifically for createdAt and seenAt
      if (sortField === 'createdAt' || sortField === 'seenAt') {
        const dateA = valA ? new Date(valA as string | Date).getTime() : 0;
        const dateB = valB ? new Date(valB as string | Date).getTime() : 0;
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      }

      // Generic comparison for other types (string, number, boolean, null, undefined)
      if (valA === null || valA === undefined) return sortDirection === 'asc' ? -1 : 1; 
      if (valB === null || valB === undefined) return sortDirection === 'asc' ? 1 : -1;

      if (typeof valA === 'string' && typeof valB === 'string') {
        return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }
      
      const comparison = valA < valB ? -1 : valA > valB ? 1 : 0;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [users, sortField, sortDirection]);

  useEffect(() => {
    setTotalPages(Math.ceil(sortedUsers.length / itemsPerPage));
    setCurrentPage(1); // Reset to first page when itemsPerPage or sortedUsers change significantly
  }, [sortedUsers.length, itemsPerPage]);

  const currentItems = useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return sortedUsers.slice(indexOfFirstItem, indexOfLastItem);
  }, [sortedUsers, currentPage, itemsPerPage]);

  const filteredUsersForSearch = users.filter(user =>
    user._id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
        const container = document.querySelector('.user-search-container');
        if (container && !container.contains(event.target as Node)) {
            setShowUserList(false);
        }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
        document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleIdClick = (user: User) => {
    navigator.clipboard.writeText(user._id);
    toast.info(`User ID ${user._id} copied to clipboard.`);
    setSelectedUserId(user._id);
    setSearchTerm(`${user.email ? `${user.email} (${user._id})` : user._id}`);
    setShowUserList(false);
    setGeneratedLink(null); // Clear previous link as user selection changed for link generation
  };

  const handleEmailClick = (email?: string) => {
    if (email) {
      navigator.clipboard.writeText(email);
      toast.info(`Email ${email} copied to clipboard.`);
    }
  };
  
  const handleRowDoubleClick = (user: User) => {
    setEditingUser(user);
    setIsEditModalOpen(true);
  };

  const handleSaveEmail = async (userId: string, newEmail: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_ADMIN_API_URL}/api/update-email`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({ userId, email: newEmail }),
      });
      if (!response.ok) {
        throw new Error('Failed to update email on server');
      }
      toast.success("Email updated successfully!");
      fetch(`${process.env.REACT_APP_ADMIN_API_URL}/api/get-users`, {
        headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` }
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }
        return response.json();
      })
      .then(data => {
        setUsers(data.users || []);
      })
      .catch(error => {
        console.error("Error updating email:", error);
        toast.error("Failed to update email. Please try again.");
      });
    } catch (error) {
      console.error("Error updating email:", error);
      toast.error("Failed to update email. Please try again.");
      return;
    }

    setIsEditModalOpen(false);
    setEditingUser(null);
  };

  const Pagination = () => {
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) { pageNumbers.push(i); }
    if (totalPages <= 1) return null; // Don't render pagination if only one page

    return (
        <div className="pagination" style={{ marginTop: '20px' }}>
            <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="pagination-button">
                ←
            </button>
            {pageNumbers.map(number => (
                <button key={number} onClick={() => setCurrentPage(number)} className={`pagination-button ${currentPage === number ? 'active' : ''}`}>
                    {number}
                </button>
            ))}
            <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="pagination-button">
                →
            </button>
            <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1);}} className="items-per-page">
                <option value="10">10 on page</option>
                <option value="20">20 on page</option>
                <option value="50">50 on page</option>
            </select>
        </div>
    );
  };

  if (isLoadingUsers && users.length === 0) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <section className="users-section">
      <div className="generate-link-container form-group" style={{ marginTop: '10px' }}>
        <div className="form-group searchable-select-container user-search-container"> 
            <label htmlFor="userSearch">Select User (for Link Generation):</label>
            <input
                type="text"
                id="userSearch"
                className="form-input"
                placeholder={isLoadingUsers ? "Loading users for search..." : "Search by ID or Email to select for link..."}
                value={searchTerm}
                onChange={(e) => {setSearchTerm(e.target.value); setShowUserList(true); setSelectedUserId(''); setGeneratedLink(null);}}
                onFocus={() => setShowUserList(true)}
                disabled={isLoadingUsers && users.length === 0}
                autoComplete="off"
            />
            {showUserList && filteredUsersForSearch.length > 0 && (
                <ul className="searchable-list">
                    {filteredUsersForSearch.map(user => ( 
                        <li
                            key={user._id}
                            onClick={() => {
                                setSelectedUserId(user._id);
                                setSearchTerm(`${user.email ? `${user.email} (${user._id})` : user._id}`); 
                                setShowUserList(false);
                                setGeneratedLink(null);
                            }}
                        >
                            {user.email ? `${user.email} (${user._id})` : user._id}
                        </li>
                    ))}
                </ul>
            )}
             {selectedUserId && !showUserList && (
                 <p className="selected-info">User selected for link: <strong>{selectedUserId}</strong></p>
             )}
        </div>
        
        <button
          onClick={handleGenerateLink}
          disabled={isGeneratingLink || !selectedUserId}
          className="submit-button" 
          style={{ marginTop: '5px' }}
        >
          {isGeneratingLink ? 'Generating...' : 'Generate Link'}
        </button>

        {generatedLink && (
          <div className="generated-link-display" style={{ marginTop: '5px' }}>
            <strong>Generated Link:</strong>
            <input
              type="text"
              readOnly
              value={generatedLink}
              className="form-input link-input" 
              style={{ marginTop: '5px', cursor: 'pointer' }} 
              onClick={() => {
                console.log("Generated link:", generatedLink);
                if (generatedLink) {
                  window.open(generatedLink, '_blank', 'noopener,noreferrer');
                }
              }}
            />
          </div>
        )}
      </div>

      {/* Users Table */}
      <div className="users-list-container" style={{ marginBottom: '30px', marginTop: '20px' }}>
        <table className="plans-table"> {/* Reusing plans-table style */}
          <thead>
            <tr>
              <th onClick={() => handleSort('storeTitle')}>Title {sortField === 'storeTitle' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('_id')}>ID {sortField === '_id' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('email')}>Email {sortField === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('productsQuantity')}>Products Qty {sortField === 'productsQuantity' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('createdAt')}>Created At {sortField === 'createdAt' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('seenAt')}>Last Visit {sortField === 'seenAt' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map(user => (
                <tr key={user._id} onDoubleClick={() => handleRowDoubleClick(user)} className="editable-row" /* Added editable-row for potential styling */ >
                <td>{user.storeTitle || 'N/A'}</td>
                <td onClick={() => handleIdClick(user)} className="clickable-cell" style={{ cursor: 'pointer' }}>{user._id}</td>
                <td onClick={() => handleEmailClick(user.email)} className="clickable-cell" style={{ cursor: 'pointer' }}>{user.email || 'N/A'}</td>
                <td>{user.productsQuantity !== undefined ? user.productsQuantity : 'N/A'}</td>
                <td>{formatDate(user.createdAt)}</td>
                <td>{formatDate(user.seenAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination />
        {currentItems.length === 0 && !isLoadingUsers && <p>No users found for current page/sort.</p>}
      </div>
      
      {/* Generate Access Link Section */}
      
      {/* Modal for Editing User Email */}
      {isEditModalOpen && editingUser && (
        <EditUserEmailModal 
          isOpen={isEditModalOpen}
          user={editingUser}
          onSave={handleSaveEmail}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingUser(null);
          }}
        />
      )}
    </section>
  );
};

export default UsersTab; 